import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { DataKeys } from "../../types";
import { useLang } from "../Context";
import { StaggerInView, StaggerInViewItem } from "../tools/Animations";
import { HealthIntegrationPreviewCard } from "../health/aimmaNurse/HealthIntegrationPreviewCard";
import { HealthSection } from "../health/HealthSection";
import { HealthSectionHeader } from "../health/HealthSectionHeader";
import { getLocalized, HealthLang } from "../health/healthTypes";
import {
    healthBody,
    healthFocusVisible,
    healthPrimaryButton,
    healthButtonArrowFa,
    healthButtonLabel,
    healthIntegrationEcosystemPreviewCardGrid,
} from "../health/healthVisualStyles";
import { educationLocalePath } from "./educationLocalePath";

interface EducationProductSuiteSectionProps {
    data: DataKeys;
}

function previewCardIdForProduct(productId: string): string {
    if (productId === "future-learning-sensei") {
        return "k12-sensei";
    }
    return productId;
}

export function EducationProductSuiteSection({ data }: EducationProductSuiteSectionProps) {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const products: DataKeys[] = data?.products ?? [];
    const sectionId = (data?.id as string) || "product-suite";
    const healthBridge = data?.healthBridge as DataKeys;
    const productLinkLabel = getLocalized(data?.productLinkLabel, hLang) || "Learn more";

    return (
        <HealthSection id={sectionId} ariaLabelledBy={`${sectionId}-title`} compact>
            <HealthSectionHeader
                eyebrow={getLocalized(data?.eyebrow, hLang)}
                title={getLocalized(data?.title, hLang)}
                titleId={`${sectionId}-title`}
                subtitle={getLocalized(data?.subtitle, hLang)}
            />
            <StaggerInView
                className={`mt-6 ${healthIntegrationEcosystemPreviewCardGrid}`}
                stagger={0.06}
            >
                {products.map((product) => {
                    const productId = product.id as string;
                    const cardId = previewCardIdForProduct(productId);
                    const comingSoon = Boolean(product.comingSoon);
                    const title = getLocalized(product.title, hLang);
                    const body = getLocalized(product.body, hLang);
                    const href = product.href
                        ? educationLocalePath(product.href as string, hLang)
                        : undefined;

                    return (
                        <StaggerInViewItem
                            key={productId}
                            direction="bottom"
                            className="h-full"
                        >
                            <HealthIntegrationPreviewCard
                                cardId={cardId}
                                groupId="doctor"
                                title={title}
                                body={body}
                                ctaLabel={productLinkLabel}
                                comingSoon={comingSoon}
                                hLang={hLang}
                                href={comingSoon ? undefined : href}
                            />
                        </StaggerInViewItem>
                    );
                })}
            </StaggerInView>
            {healthBridge && (
                <div className="mt-8 flex flex-col gap-3 border-t border-grey-100 pt-6 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
                    <p className={`${healthBody} min-w-0 flex-1 text-sm text-grey-600`}>
                        {getLocalized(healthBridge.label, hLang)}
                    </p>
                    <Link
                        to={educationLocalePath(
                            (healthBridge.healthHref as string) ?? "/health",
                            hLang
                        )}
                        className={`${healthPrimaryButton} ${healthFocusVisible} !w-auto max-w-none shrink-0 self-start whitespace-nowrap lg:self-auto`}
                    >
                        <span className={healthButtonLabel}>
                            {getLocalized(healthBridge.healthLabel, hLang)}
                        </span>
                        <FontAwesomeIcon icon={faArrowRight} className={healthButtonArrowFa} />
                    </Link>
                </div>
            )}
        </HealthSection>
    );
}
